import { Injectable } from '@angular/core';
import { CiprsRole, UserProfile } from './user';
import { Subject, Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { map, catchError } from 'rxjs/operators';

enum SessionStorageKey {
  Token = 'ciprs_token',
  User = 'ciprs_user',
  Expiration_time_ms = 'ciprs_expiration'
}

@Injectable()
export class AuthenticationService {

  private userSource = new Subject<UserProfile>();
  authenticatedUserChange$: Observable<UserProfile> = this.userSource.asObservable();

  private patAuthenticationSource = new Subject<boolean>();
  patAuthentication$: Observable<boolean> = this.patAuthenticationSource.asObservable();

  private tokenExpirationSource = new Subject<number>();
  tokenExpirationChange$: Observable<number> = this.tokenExpirationSource.asObservable();

  private cachedUserProfile: UserProfile;

  constructor(private http: HttpClient) {
    if (this.isValidSessionInSessionStorage()) {
      this.restoreCurrentUserFromSessionStorage();
    }
  }

  /**
   * Given the url fragment returned by the auth server's intial redirect, get user profile from auth server. Without
   * this additional call to the backend, subsequent calls to the secure endpoints will fail (even when provided the bearer token).
   * @param {string} hash The hash provided by calling the auth server's /authorize endpoint (including the #)
   * @return {Promise<boolean>} A Promise indicating whether the user profile was successfully received and the bearer token
   * is ready to be used.
   */
  processOAuthHash(hash: string): Promise<boolean> {
    const token = this.getAccessToken(hash);
    if (!token) {
      return new Promise<boolean>((resolve, reject) => {resolve(false)});
    }

    // TODO: verify this code!!!!
    // This allows the check_token call to use the new token when re-authenticating
    if (this.getToken()) {
      this.setInSessionStorage(SessionStorageKey.Token, token);
    }

    return new Promise<boolean>((resolve, reject) => {
      this.getAuthorizationInfoFromAuthServer(token)
        .subscribe((auth: AuthorizationServerResponse) => {

          const expiresInSeconds = this.getExpiresInSeconds(hash);
          this.tokenExpirationSource.next(expiresInSeconds * 1000);

          this.setCurrentUser(auth.userProfile,
                              token,
                              auth.expiration);
          resolve(true);
        }, (error) => {
          console.error(error);
          // TODO: verify this code!!!!
          this.clearSession();
          resolve(false);
      });
    });
  }

  private getAccessToken(hash: string): string | undefined {
    const matches = hash.match(/access_token=([^&]*)/);
    if (matches.length < 2) {
      console.error(`${hash} doesn't contain an access_token.`);
      return undefined;
    }
    return matches[1];
  }
  private getExpiresInSeconds(hash: string): number {
    const matches = hash.match(/expires_in=([^&]*)/);
    if (matches.length < 2) {
      console.error(`${hash} doesn't contain an expires_in parameter.`);
      return 0;
    }
    return Number(matches[1]);
  }

  private getAuthorizationInfoFromAuthServer(token: string): Observable<AuthorizationServerResponse> {
    let headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    headers = headers.append('Content-Type', 'application/x-www-form-urlencoded');
    const state = this.createGuid();
    let body = new HttpParams();
    body = body.append('state', state);
    body = body.append('token', token);

    return this.http.post<AuthorizationServerResponse>(environment.security.check_token_uri, body.toString(), { headers })
      .pipe(
        map(authorizationServerResponse => {
          // Try to detect CSRF issues
          if (state !== authorizationServerResponse.state) {
            return Observable.throw('A security issue has been detected. Please log in again.');
          }
          return authorizationServerResponse;
        }),
        catchError(error => Observable.throw(error))
      )
  }

  authenticateViaClientCertificate(): Promise<boolean> {

    const existingIframe = document.getElementById('client-cert-authentication-iframe');
    if (existingIframe) {
      return Promise.resolve(false);
    }

    const parms = this.buildParmsMapForAuthServerPost();

    const iframe = document.createElement('iframe');
    iframe.id = 'client-cert-authentication-iframe';
    iframe.setAttribute('style', 'width:0px;height:0px;border:none');
    const html = `<body><form method="post" action="${environment.security.client_certificate_auth_uri}">` +
                    `<input type="hidden" name="response_type" value="token"/>` +
                    `<input type="hidden" name="redirect_uri" value="${parms.get('redirect_uri')}"/>` +
                    `<input type="hidden" name="client_id" value="${parms.get('client_id')}"/>` +
                  `</form></body>`;
    document.body.appendChild(iframe);
    iframe.contentWindow.document.open();
    iframe.contentWindow.document.write(html);
    iframe.contentWindow.document.close();
    const f = iframe.contentWindow.document.getElementsByTagName('form')[0];
    f.submit();

    const MAXIMUM_ITERATIONS = 20;
    const POLLING_INTERVAL_MS = 250;
    return new Promise<boolean>((resolve, reject) => {
      let iteration = 0;
      const intervalId = setInterval(() => {
        iteration++;
        const iFrameHash = this.getIFrameHash(iframe);
        if (iFrameHash) {
          console.log(`${new Date().toString()} iFrameHash=${iFrameHash} after iteration ${iteration * POLLING_INTERVAL_MS} milliseconds`);
          iframe.parentNode.removeChild(iframe);

          clearInterval(intervalId);
          this.processOAuthHash(iFrameHash).then((value) => {
            this.patAuthenticationSource.next(value);
            resolve(value);
          });
        }
        if (iteration === MAXIMUM_ITERATIONS) {
          console.error(`${new Date().toString()} failed to authenticate via client cert after ${iteration * POLLING_INTERVAL_MS} ms`);
          iframe.parentNode.removeChild(iframe);
          clearInterval(intervalId);
          this.patAuthenticationSource.next(false);
          resolve(false);
       }
      }, POLLING_INTERVAL_MS);
    });
  }

  private getIFrameHash(iframe: HTMLIFrameElement): string | undefined {
    try {
      const url = iframe.contentWindow.location.href;
      const hashIndex = url.indexOf('#');
      return hashIndex !== -1 ? url.substr(hashIndex) : undefined;
    } catch (e) {
      return undefined;
    }
  }

  logOut(): void {
    this.http.post(environment.security.revoke_token_uri, { })
      .subscribe((response) => {
          console.log(response);
        }, (error) => {
          console.error(error);
      });
    this.clearSession();
  }

  /**
   * Open a child window to the authorization server.
   * @return {Promise<boolean>} A Promise indicating whether the user successfully authenticated.
   */
  openChildWindowToAuthorizationServer(): Promise<boolean> {
    const parms = this.buildParmsMapForAuthServerPost();
    const childWindow = this.openChildWindow();
    let doc;
    try {
      doc = childWindow.document;
    } catch (e) { // TODO: 20180312 see if this logic still makes sense once this code is on the same domain as the auth server
      console.log('window already open');
      return new Promise<boolean>((resolve, reject) => resolve(false));
    }

    // close the child window if the parent goes away (due to window refresh/close)
    const unloadEventListener = () => { childWindow.close(); }
    window.addEventListener('unload', unloadEventListener);

    // create a form in the child window and post it to the auth server
    this.createAndSubmitForm(doc, this.getAuthUri(), parms);

    const pollingPromise = this.pollChildWindowForHashOrClosure(childWindow);
    return pollingPromise.then(value => {
      window.removeEventListener('unload', unloadEventListener);
      return value;
    });
  }

  private buildParmsMapForAuthServerPost(): Map<string, string> {
    const parms: Map<string, string> = new Map<string, string>();
    parms.set('response_type', 'token');
    parms.set('redirect_uri', this.getRedirectUri());
    parms.set('client_id', environment.security.client_id);
    return parms;
  }

  private getAuthUri(): string {
    // Docker build will have production=true and will have a relative auth uri
    if (environment.production) {
      return window.location.origin + environment.security.auth_uri;
    } else {
      return environment.security.auth_uri;
    }
  }

  private getRedirectUri(): string {
    const $base = $('base');
    const baseHref = $base.attr('href');
    const baseUriWithTrailingSlash = window.location.origin + baseHref;
    const baseUri = baseUriWithTrailingSlash.substr(0, baseUriWithTrailingSlash.length - 1)
    return baseUri + environment.security.redirect_uri;
  }

  private openChildWindow(): Window {
    const parentWidth = window.outerWidth;
    const parentHeight = window.outerHeight;
    const childWidth =  Math.max(Math.ceil(parentWidth / 2), 400);
    const childHeight = Math.max(Math.ceil(parentHeight / 2), 400);

    const childWindow = window.open(this.getRedirectUri(),
                                    'NCAOC Auth Server',
                                    `scrollbars=yes,resizable=yes,top=50,left=50,width=${childWidth},height=${childHeight}`);
    return childWindow;
  }

  private pollChildWindowForHashOrClosure(childWindow: Window, pollingInterval = 250): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const intervalId = setInterval(() => {

        if (childWindow.closed) {
          console.log(new Date().toISOString() + ' window was closed');
          resolve(false);
          clearInterval(intervalId);
        } else {
          const childWindowHash = this.getChildWindowHash(childWindow);
          if (childWindowHash) {
            console.log(new Date().toString() + ' childWindowHash=' + childWindowHash);
            childWindow.close();
            clearInterval(intervalId);
            this.processOAuthHash(childWindowHash).then(() => {
              resolve(true);
            });
          }
        }
      }, pollingInterval);
    });
  }

  private createAndSubmitForm(doc: Document, path: string, paramsMap: Map<string, string>) {
    const form = doc.createElement('form');
    form.setAttribute('method', 'post');
    form.setAttribute('action', path);

    paramsMap.forEach((value, key) => {
      const hiddenField = doc.createElement('input');
      hiddenField.setAttribute('type', 'hidden');
      hiddenField.setAttribute('name', key);
      hiddenField.setAttribute('value', value);

      form.appendChild(hiddenField);
    });

    doc.body.appendChild(form);
    form.submit();
  }

  private getChildWindowHash(win: Window): string | undefined {
    try {
      return win.location.hash;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * This will create an rfc4122 version 4 compliant guid
   */
  private createGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      /* tslint:disable:no-bitwise */
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      /* tslint:enable:no-bitwise */

      return v.toString(16);
    });
  }

  getCurrentUser(): UserProfile | null {
    return this.cachedUserProfile;
  }

  isValidSessionInSessionStorage(): boolean {
    const storedExpirationTimeMs = this.getFromSessionStorage(SessionStorageKey.Expiration_time_ms); // may be null
    return storedExpirationTimeMs &&
           Number(storedExpirationTimeMs) > (new Date()).getTime();
  }

  private restoreCurrentUserFromSessionStorage(): void {
    const userProfile = JSON.parse(this.getFromSessionStorage(SessionStorageKey.User));
    const token = this.getFromSessionStorage(SessionStorageKey.Token);
    const expirationTimeMs = Number(this.getFromSessionStorage(SessionStorageKey.Expiration_time_ms));
    this.setCurrentUser(userProfile, token, expirationTimeMs);
}

  private setCurrentUser(user: UserProfile, token: string, expirationTime: number) {
    this.cachedUserProfile = user;
    this.setInSessionStorage(SessionStorageKey.Token, token);
    this.setInSessionStorage(SessionStorageKey.User, JSON.stringify(user));
    this.setInSessionStorage(SessionStorageKey.Expiration_time_ms, expirationTime.toString());
    this.userSource.next(user);
  }

  clearSession(): void {
    this.cachedUserProfile = null;
    this.removeFromSessionStorage(SessionStorageKey.Token);
    this.removeFromSessionStorage(SessionStorageKey.User);
    this.removeFromSessionStorage(SessionStorageKey.Expiration_time_ms);
    this.userSource.next(null);
    this.tokenExpirationSource.next(null);
  }

  isUserLoggedIn(): boolean {
    // TODO: should we consider whether the token is expired?
    return !!this.cachedUserProfile; // use cachedUserProfile as a proxy for being logged in
    // return !!this.getFromSessionStorage(SessionStorageKey.Token);
  }

  isUserInRole(ciprsRole: CiprsRole): boolean {
    const currentUser = this.getCurrentUser();
    return !!currentUser && currentUser.roleName === ciprsRole;
  }
  isUserInRoles(...ciprsRoles: CiprsRole[]): boolean {
    const currentUser = this.getCurrentUser();
    return !!currentUser && ciprsRoles.indexOf(currentUser.roleName) !== -1;
  }

  getToken(): string {
    return this.getFromSessionStorage(SessionStorageKey.Token);
  }

  private setInSessionStorage(key: SessionStorageKey, value: string) {
    window.sessionStorage.setItem(key, value);
  }
  private getFromSessionStorage(key: SessionStorageKey): string {
    return window.sessionStorage.getItem(key);
  }
  private removeFromSessionStorage(key: SessionStorageKey): void {
    window.sessionStorage.removeItem(key);
  }
}

interface AuthorizationServerResponse { // TODO: finalize this and refactor it out of here
  aud: string[];
  authorities: string[];
  client_id: string;
  exp: number; // expiration time in secconds
  expiration: number; // expiration time in ms
  scope: string[];
  state: string;
  userProfile: UserProfile;
  user_name: string;
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/pipeline-ciprs-ui-final/project/src/app/shared/authentication.service.ts